import { GridOptions, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, Optional } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  ItemDetail,
  ItemDetailStatusEnum,
  PoDetail,
  ShipBin,
} from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { AgStyleFns } from "app/shared/ag-style-fns";
import { NavFns } from "app/shared/nav-fns";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { ItemDetailLogDialogComponent } from "app/shared/item-detail-log-dialog.component";
import { UniAgFns } from "app/shared/uni-ag-fns";
import { UnitOfWork } from "app/shared/unit-of-work";
import _ from "lodash";
import { ProcessManagerComponent } from "./process-manager.component";
import { ShipBinAddonDialogComponent } from "./ship-bin-addon-dialog.component";
import { ShipBinCheckoutDialogComponent } from "./ship-bin-checkout-dialog.component";
import { AgFinancialCharts } from "ag-charts-angular";

@Component({
  selector: "app-ship-bin-management",
  templateUrl: "./ship-bin-management.component.html",
  standalone: false,
})
export class ShipBinManagementComponent extends DomainBaseComponent {
  binGridOptions: GridOptionsExt;
  shipBins: ShipBin[] = [];
  selectedShipBin: ShipBin;

  detailGridOptions: GridOptionsExt;
  binItemDetails: ItemDetail[] = [];

  selectedJoHeaderId: number | null = null;

  inProcessGridOptions: GridOptionsExt;
  inProcessItems: ItemDetail[] = [];

  backorderGridOptions: GridOptionsExt;
  backorderItems: PoDetail[] = [];

  constructor(
    public override uow: UnitOfWork,
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    @Optional() private processManager: ProcessManagerComponent,
  ) {
    super(domainService);

    if (this.processManager) {
      setTimeout(() => {
        this.processManager.showShipFrequenciesButton = true;
      });
    }
  }

  override ngOnDestroy() {
    if (this.processManager) {
      this.processManager.showShipFrequenciesButton = false;
    }
    super.ngOnDestroy();
  }

  ngOnInit() {
    super.ngOnInit();
    this.updateFromContext();
  }

  async updateFromContext() {
    this.binGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onBinGridReady,
      onRowSelected: this.onBinRowSelected,
      onCellValueChanged: this.onBinCellValueChanged,
      suppressNoRowsOverlay: true,
    });

    this.detailGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onDetailGridReady,
      onRowSelected: this.onDetailRowSelected,
      suppressNoRowsOverlay: true,
    });

    this.inProcessGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onInProcessGridReady,
      suppressNoRowsOverlay: true,
    });

    this.backorderGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onBackorderGridReady,
      suppressNoRowsOverlay: true,
    });

    this.isPageReady = true;
  }

  onBinGridReady = async (e: any) => {
    const colDefs = [
      { headerName: "Bin #", field: "id", ...AgFns.fixWidth(100) },
      {
        ...AgFns.createButtonProps("", this.showCheckout.bind(this), {
          label: "Check Out",
          canDisplay: (bin: ShipBin) => bin?.itemDetails?.length > 0,
        }),
        cellStyle: { display: "flex", "justify-content": "center" },
        ...AgFns.fixWidth(100),
      },
      {
        ...AgFns.createButtonProps("", this.toggleBinFull.bind(this), {
          calcLabel: (bin: ShipBin) => bin?.isBinFull ? "Mark Not Full" : "Mark Full",
        }),
        cellStyle: { display: "flex", "justify-content": "center" },
        ...AgFns.fixWidth(110),
      },
      {
        ...AgFns.createButtonProps("", this.closeBin.bind(this), {
          label: "Close Bin",
        }),
        cellStyle: { display: "flex", "justify-content": "center" },
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps("", this.showAddonItems.bind(this), {
          label: "In-Process",
        }),
        cellStyle: { display: "flex", "justify-content": "center" },
        ...AgFns.fixWidth(90),
      },
      { headerName: "Priority", field: "priorityOrder", editable: true, ...AgFns.fixWidth(130) },
      { headerName: "Account", valueGetter: (p) => this.getAccountName(p.data), filter: "agTextColumnFilter", ...AgFns.flexWidth(150) },
      { headerName: "Ship To", valueGetter: (p) => this.getShippingAddress(p.data), ...AgFns.flexWidth(200) },
      { headerName: "Item Count", valueGetter: (p) => p.data?.itemDetails?.length ?? 0, ...AgFns.fixWidth(150) },
      { headerName: "Frequency", valueGetter: (p) => this.dbQueryService.lookup("ShipFrequency", p.data?.shipFrequencyId), ...AgFns.fixWidth(160) },
      { headerName: "Exp. Ship Date", field: "expectedShipDate", cellClass: (p) => AgStyleFns.getDueDateCellClass(p.value), ...AgFns.fixWidth(180) },
      { headerName: "Bin Full?", field: "isBinFull", ...AgFns.fixWidth(120) },
    ];
    AgFns.initGrid(e, colDefs, [{ colId: "priorityOrder", sort: "asc" }]);

    await this.loadShipBins();
  };

  onDetailGridReady = (e: any) => {
    const colDefs = [
      {
        headerName: "Item ID",
        field: "id",
        cellClass: AgStyleFns.getLinkCellClass() + " ag-left-aligned-cell",
        onCellClicked: (p: any) => this.openItemStory(p.data as ItemDetail),
        tooltipValueGetter: () => "Show this item's story",
        ...AgFns.fixWidth(150),
      },
      {
        headerName: "J/O #",
        ...NavFns.createIdCellClickedNavProps("joDetail.joHeader.id", this.router, "/in-process/manage/job-orders"),
        ...AgFns.fixWidth(100),
      },
      { headerName: "JO Date", field: "joDetail.joHeader.joDate", ...AgFns.fixWidth(120) },
      { headerName: "Exp. Ship Date", field: "joDetail.joHeader.origExpectedShipDate", cellClass: (p) => AgStyleFns.getDueDateCellClass(p.value), ...AgFns.fixWidth(160) },
      { headerName: "Account", valueGetter: (p) => p.data?.joDetail?.joHeader?.account?.accountName, ...AgFns.flexWidth(120) },
      { headerName: "Product", valueGetter: (p) => p.data?.product?.productType?.description, ...AgFns.flexWidth(150) },
      { headerName: "Manufacturer", valueGetter: (p) => p.data?.product?.productType?.manufacturer?.name, ...AgFns.flexWidth(120) },
      { headerName: "Status", field: "itemDetailStatus.name", ...AgFns.flexWidth(100) },
      {
        ...UniAgFns.getEntityDeleteColDef(this.removeFromBin.bind(this), {
          canDisplay: (itd: ItemDetail) => !!itd && !itd.isShippableGoods && itd.invoiceHeaderId == null,
          tooltip: "Remove this item from the bin.",
        }),
      },
    ];
    AgFns.initGrid(e, colDefs);
  };

  onDetailRowSelected = async (e: RowSelectedEvent) => {
    if (!e.node.isSelected()) return;
    const itd = e.data as ItemDetail;
    const joHeaderId = itd?.joDetail?.joHeader?.id;
    if (joHeaderId && joHeaderId !== this.selectedJoHeaderId) {
      this.selectedJoHeaderId = joHeaderId;
      await this.loadJoStatusPanels(joHeaderId);
    }
  };

  onInProcessGridReady = (e: any) => {
    const colDefs = [
      {
        headerName: "Item ID",
        field: "id",
        cellClass: AgStyleFns.getLinkCellClass() + " ag-left-aligned-cell",
        onCellClicked: (p: any) => this.openItemStory(p.data as ItemDetail),
        tooltipValueGetter: () => "Show this item's story",
        ...AgFns.fixWidth(150),
      },
      { headerName: "Product", valueGetter: (p) => p.data?.product?.productType?.description, ...AgFns.flexWidth(200) },
      { headerName: "Manufacturer", valueGetter: (p) => p.data?.product?.productType?.manufacturer?.name, ...AgFns.flexWidth(150) },
      { headerName: "Features", field: "product.featureChoicesExtract", ...AgFns.flexWidth(150) },
      { headerName: "Status", field: "itemDetailStatus.name", ...AgFns.fixWidth("status") },
    ];
    AgFns.initGrid(e, colDefs);
  };

  onBackorderGridReady = (e: any) => {
    const colDefs = [
      { headerName: "PO #", field: "poHeaderId", ...AgFns.fixWidth(100) },
      { headerName: "Manufacturer", valueGetter: (p) => p.data?.poHeader?.manufacturer?.name, ...AgFns.flexWidth(150) },
      { headerName: "Product", valueGetter: (p) => p.data?.product?.productType?.description, ...AgFns.flexWidth(200) },
      { headerName: "Features", field: "product.featureChoicesExtract", ...AgFns.flexWidth(150) },
      { headerName: "Order Qty", field: "orderQty", ...AgFns.fixWidth("qty") },
      { headerName: "Remaining", valueGetter: (p) => p.data?.getRemainingQty(), ...AgFns.fixWidth("qty") },
      { headerName: "Exp. Ship Date", field: "expectedShipDate", cellClass: (p) => AgStyleFns.getDueDateCellClass(p.value), ...AgFns.fixWidth("date") },
      { headerName: "PO Status", field: "poHeader.poStatus.name", ...AgFns.fixWidth("status") },
    ];
    AgFns.initGrid(e, colDefs);
  };

  async loadJoStatusPanels(joHeaderId: number) {
    const [inProcess, backorders] = await Promise.all([
      this.dbQueryService.getInProcessItemDetailsByJoHeaderId(joHeaderId),
      this.dbQueryService.getBackorderPoDetailsByJoHeaderId(joHeaderId),
    ]);
    this.inProcessItems = inProcess;
    this.backorderItems = backorders;
    AgFns.refreshGrid(this.inProcessGridOptions, this.inProcessItems);
    AgFns.refreshGrid(this.backorderGridOptions, this.backorderItems);
  }

  async loadShipBins() {
    this.binGridOptions.apiX?.showLoadingOverlay();
    this.shipBins = await this.dbQueryService.getOpenShipBins();
    AgFns.refreshGrid(this.binGridOptions, this.shipBins);
    await AgFns.selectFirstRow(this.binGridOptions);
  }

  onBinRowSelected = async (e: RowSelectedEvent) => {
    if (!e.node.isSelected()) return;
    this.selectedShipBin = e.data as ShipBin;
    await this.loadBinDetails(this.selectedShipBin.id);
  };

  async loadBinDetails(shipBinId: number) {
    this.binItemDetails = await this.dbQueryService.getShipBinItemDetails(shipBinId);
    AgFns.refreshGrid(this.detailGridOptions, this.binItemDetails);
    this.selectedJoHeaderId = null;
    this.clearJoStatusPanels();
    await AgFns.selectFirstRow(this.detailGridOptions);
  }

  clearJoStatusPanels() {
    this.inProcessItems = [];
    this.backorderItems = [];
    AgFns.refreshGrid(this.inProcessGridOptions, this.inProcessItems);
    AgFns.refreshGrid(this.backorderGridOptions, this.backorderItems);
  }

  getAccountName(shipBin: ShipBin): string {
    if (!shipBin) return "";
    const invh = _.first(shipBin.invoiceHeaders);
    if (invh?.joHeader?.account) return invh.joHeader.account.accountName;
    // Fallback: check item details
    const itd = _.first(shipBin.itemDetails);
    if (itd?.joDetail?.joHeader?.account) return itd.joDetail.joHeader.account.accountName;
    return "";
  }

  getShippingAddress(shipBin: ShipBin): string {
    if (!shipBin) return "";
    const invh = _.first(shipBin.invoiceHeaders);
    const joh = invh?.joHeader;
    if (!joh) {
      const itd = _.first(shipBin.itemDetails);
      const joh2 = itd?.joDetail?.joHeader;
      if (!joh2) return "";
      return [joh2.shippingName, joh2.shippingCity, joh2.shippingState].filter(Boolean).join(", ");
    }
    return [joh.shippingName, joh.shippingCity, joh.shippingState].filter(Boolean).join(", ");
  }

  onBinCellValueChanged = async (event: any) => {
    if (event.colDef.field === "priorityOrder") {
      const shipBin = event.data as ShipBin;
      try {
        await this.dbSaveService.saveSelectedChanges([shipBin]);
        this.toastr.success(`Priority updated to ${shipBin.priorityOrder}`);
      } catch (error) {
        this.toastr.error("Failed to save priority.");
        shipBin.entityAspect.rejectChanges();
        this.binGridOptions.apiX?.refreshCells();
      }
    }
  };

  private isCheckoutOpening = false;
  async showCheckout(shipBin: ShipBin) {
    if (!shipBin) return;
    if (this.isCheckoutOpening) return;
    if (!shipBin.isOpen) return;
    this.isCheckoutOpening = true;
    try {
      const released = await ShipBinCheckoutDialogComponent.openDialog(
        this.pngDialogService,
        { shipBin },
      );
      if (released) {
        await this.loadShipBins();
        if (this.selectedShipBin?.id === shipBin.id) {
          this.binItemDetails = [];
          AgFns.refreshGrid(this.detailGridOptions, this.binItemDetails);
          this.selectedShipBin = null;
        }
      }
    } finally {
      this.isCheckoutOpening = false;
    }
  }

  async showAddonItems(shipBin: ShipBin) {
    if (!shipBin) return;
    await ShipBinAddonDialogComponent.openDialog(
      this.pngDialogService,
      { shipBin },
    );
  }

  async toggleBinFull(shipBin: ShipBin) {
    if (!shipBin) return;
    shipBin.isBinFull = !shipBin.isBinFull;
    await this.dbSaveService.saveChanges();
    this.toastr.success(shipBin.isBinFull ? "Bin marked as full" : "Bin marked as not full");
    AgFns.refreshGrid(this.binGridOptions, this.shipBins);
  }

  private isRemovingFromBin = false;
  async removeFromBin(itd: ItemDetail) {
    if (!itd || itd.isShippableGoods || itd.invoiceHeaderId != null) return;
    if (this.isRemovingFromBin) return;
    this.isRemovingFromBin = true;
    try {
      const binId = itd.shipBinId;
      const ok = await this.dialogService.yesNoDialog(
        "Remove Item From Bin",
        `The condition of item ${itd.id} is '${itd.itemDetailStatus?.name}'. ` +
          `Take the item out of the physical bin ${binId}. ` +
          `The item keeps its condition and stays on the job order. Do you want to continue?`,
      );
      if (!ok) return;

      itd.shipBinId = null;
      const saved = await this.dbSaveService.saveChanges();
      if (!saved) {
        itd.entityAspect.rejectChanges();
        return;
      }
      this.toastr.success(`The system removed item ${itd.id} from bin ${binId}.`);
      await this.loadBinDetails(binId);
      // Item Count is a valueGetter over the Breeze nav collection, which Breeze
      // has already updated - only the cell needs to be redrawn.
      this.binGridOptions.apiX?.refreshCells({ force: true });
    } finally {
      this.isRemovingFromBin = false;
    }
  }

  async closeBin(shipBin: ShipBin) {
    if (!shipBin) return;

    const itemDetails = await this.dbQueryService.getShipBinItemDetails(shipBin.id);
    if (itemDetails.length > 0) {
      const ok = await this.dialogService.yesNoDialog(
        "Close Bin",
        `This bin has ${itemDetails.length} item(s). The system returns the good items to Pending Ship Prep. ` +
          `Damaged and missing items keep their condition. Do you want to close the bin?`,
      );
      if (!ok) return;

      itemDetails.forEach((itd) => {
        if (itd.isShippableGoods) {
          itd.itemDetailStatusId = ItemDetailStatusEnum.InProcessPendingShipPrep;
        }
        itd.shipBinId = null;
      });
    }

    shipBin.isOpen = false;
    await this.dbSaveService.saveChanges();
    this.toastr.success("Bin closed");
    await this.loadShipBins();
    if (this.selectedShipBin?.id === shipBin.id) {
      this.binItemDetails = [];
      AgFns.refreshGrid(this.detailGridOptions, this.binItemDetails);
      this.selectedShipBin = null;
    }
  }

  async openItemStory(itd: ItemDetail) {
    if (!itd) return;
    await DialogBaseComponent.open(ItemDetailLogDialogComponent, this, { itemDetail: itd });
  }
}
